import { useMemo, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

import Box from "@mui/material/Box";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemText,
  ListItemButton,
  Typography,
  IconButton,
} from "@mui/material";
import { useRouter } from "@tanstack/react-router";
import config from "@/config";
import { useNavigationUtils } from "@/utils/navigationHooks";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { setVerified } from "@/redux/slices/VerifySlice";
import CardInfo from "@/components/Card";
import { useTransactionSnackbar } from "@/hooks/useTransactionSnackbar";
interface UserInfoProps {
  onMenuItemClick?: () => void;
}

interface WalletAddress {
  type: string;
  address: string;
}

export default function UserInfo({ onMenuItemClick }: UserInfoProps) {
  const [open, setOpen] = useState(false);
  const privyState = usePrivy();
  const router = useRouter();
  const { t } = useTranslation();
  const { showTransactionSnackbar } = useTransactionSnackbar();

  const { user } = useMemo(
    () => ({
      user: privyState.user,
      logout: privyState.logout,
    }),
    [privyState.user]
  );

  // Get wallet addresses
  const walletAddresses = useMemo(() => {
    const addresses: WalletAddress[] = [];

    // Add EVM address if available
    if (user?.wallet?.address) {
      addresses.push({
        type: "EVM",
        address: user.wallet.address,
      });
    }

    return addresses;
  }, [user?.wallet?.address]);

  const { handleLogout } = useNavigationUtils();

  const isVerifyPath = router.state.location.pathname === "/verify";
  const isNotFoundPath = router.state.location.pathname === "/not-found";

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleProfile = () => {
    handleClose(); // Close dialog
    onMenuItemClick?.(); // Close mobile navigation menu
    router.navigate({ to: config.PATHNAME.USER_PROFILE });
  };

  const handleLogoutClick = () => {
    handleClose(); // Close dialog
    onMenuItemClick?.(); // Close mobile navigation menu
    handleLogout();
    setVerified(false); // Reset verification state
  };

  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        showTransactionSnackbar({
          status: "success",
          title: "Copied!",
          message: `${type} address copied to clipboard`,
        });
      },
      (err) => {
        console.error("Could not copy text: ", err);
        showTransactionSnackbar({
          status: "error",
          title: "Error",
          message: "Failed to copy address",
        });
      }
    );
  };

  const formatAddress = (address: string) => {
    if (!address) return "";
    if (address.includes("...")) return address; // Already formatted
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  return (
    <CardInfo>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          textAlign: "center",
          "&:hover": {
            borderRadius: "18px",
          },
        }}
      >
        <Tooltip title="Account">
          <Button
            onClick={handleClick}
            size="small"
            aria-haspopup="true"
            sx={{
              display: "flex",
              padding: 1,
              paddingLeft: 2,
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.1)",
                borderRadius: "18px",
              },
              transition: "border 0.3s ease",
            }}
          >
            <span className="text-white capitalize">
              {user?.wallet?.address
                ? formatAddress(user.wallet.address)
                : "Not available"}
            </span>
            <KeyboardArrowDownIcon sx={{ color: "white" }} />
          </Button>
        </Tooltip>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "400px",
            borderRadius: "18px",
            bgcolor: "#1E1E1E", // Dark background matching the image
          },
        }}
      >
        <Box sx={{ px: 3, py: 2, bgcolor: "#1E1E1E", border: "1px solid #333", borderRadius: "18px" }}>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 0,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: "white" }}>
              Your wallet
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                color: "white",
                p: 0,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            {/* Wallet Addresses */}
            <Box sx={{ mb: 2 }}>
              {walletAddresses.map((wallet, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: index < walletAddresses.length - 1 ? 1 : 0,
                    borderRadius: "12px",
                    bgcolor: "rgba(255,255,255,0.05)",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "grey.500", display: "block" }}
                    >
                      {wallet.type}
                    </Typography>
                    <Typography sx={{ color: "white" }}>
                      {formatAddress(wallet.address)}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => copyToClipboard(wallet.address, wallet.type)}
                    sx={{ color: "grey.400" }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>

            {/* Menu Options */}
            <List sx={{ p: 0 }}>
              {/* Profile Option */}
              <ListItemButton
                onClick={handleProfile}
                disabled={isVerifyPath || isNotFoundPath}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  mb: 1,
                  bgcolor: "rgba(255,255,255,0.05)",
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AccountCircleIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("navbar.menu.profile")}
                  sx={{ "& .MuiListItemText-primary": { color: "white" } }}
                />
                <ChevronRightIcon sx={{ color: "grey.500" }} />
              </ListItemButton>

              {/* Disconnect Option */}
              <ListItemButton
                onClick={handleLogoutClick}
                sx={{
                  borderRadius: "12px",
                  py: 1.5,
                  bgcolor: "rgba(255,255,255,0.05)",
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PowerSettingsNewIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("navbar.menu.logout")}
                  sx={{ "& .MuiListItemText-primary": { color: "white" } }}
                />
                <ChevronRightIcon sx={{ color: "grey.500" }} />
              </ListItemButton>
            </List>
          </DialogContent>
        </Box>
      </Dialog>
    </CardInfo>
  );
}
